import { useState, useEffect, useRef } from "react";
import { cases } from "@/mocks/cases";

export default function CasesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const next = () => setCurrentIndex((i) => (i + 1) % cases.length);
  const prev = () => setCurrentIndex((i) => (i - 1 + cases.length) % cases.length);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
    }
  }, [currentIndex]);

  return (
    <section
      id="cases"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-background-50"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="reveal opacity-0 inline-block px-4 py-1.5 text-xs font-medium tracking-widest uppercase bg-primary-100 text-primary-700 rounded-full mb-4">
            Результаты работы
          </span>
          <h2 className="reveal opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-900 mb-4">
            Кейсы работы с нами
          </h2>
          <p className="reveal opacity-0 text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
            Конкретные задачи, решения и результаты наших партнёров
          </p>
        </div>

        {/* Slider */}
        <div className="reveal opacity-0 relative overflow-hidden">
          <div
            ref={sliderRef}
            className="flex transition-transform duration-500 ease-out"
          >
            {cases.map((caseItem) => (
              <div
                key={caseItem.id}
                className="w-full flex-shrink-0 px-0 md:px-2"
              >
                <div className="flex flex-col md:flex-row gap-6 md:gap-8 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300/60 transition-all duration-300">
                  {/* Image */}
                  <div className="md:w-1/2 shrink-0">
                    <div className="h-56 md:h-80 w-full overflow-hidden">
                      <img
                        src={caseItem.imageUrl}
                        alt={caseItem.partnerName}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 w-fit">
                      {caseItem.partnerName}
                    </span>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-1">
                          Проблема
                        </h4>
                        <p className="text-sm md:text-base text-foreground-700">
                          {caseItem.challenge}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-1">
                          Решение
                        </h4>
                        <p className="text-sm md:text-base text-foreground-700">
                          {caseItem.solution}
                        </p>
                      </div>

                      <div className="pt-3 border-t border-background-200/70">
                        <h4 className="text-xs font-medium text-accent-700 uppercase tracking-wider mb-1">
                          Результат
                        </h4>
                        <p className="text-sm md:text-base text-foreground-900 font-medium">
                          {caseItem.result}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prev}
            className="absolute left-0 md:left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/90 backdrop-blur-sm border border-background-300/60 text-foreground-700 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-all shadow-md z-10"
            aria-label="Предыдущий кейс"
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl" />
          </button>
          <button
            onClick={next}
            className="absolute right-0 md:right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/90 backdrop-blur-sm border border-background-300/60 text-foreground-700 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-all shadow-md z-10"
            aria-label="Следующий кейс"
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl" />
          </button>
        </div>

        {/* Dots */}
        <div className="reveal opacity-0 flex items-center justify-center gap-2 mt-6">
          {cases.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? "bg-primary-500 w-8"
                  : "bg-background-300 hover:bg-background-400"
              }`}
              aria-label={`Перейти к кейсу ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}